import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CopyIcon,
  DuplicateIcon,
  EditIcon,
  IconButton,
  List,
  ListItem,
  ListSeparator,
  Menu,
  Sheet,
  TaskListCompleteIcon,
  TrashIcon,
  TreeDotsIcon,
  CheckboxBlankTogglerIcon,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { MOVE_TASK_LIST_TO_PROJECT_SUCCESS } from "~/store/action_types/index.jsx";
import { moveTaskListToProject } from "~/store/actions/actions.taskLists.jsx";
import { TaskListDuplicateDialog } from "./TaskListDuplicateDialog.jsx";
import ConfirmLink from "../component.confirm_link.jsx";
import PrefetchAllProjectsContainer from "../project/PrefetchAllProjectsContainer.jsx";
import { showErrorToast, showSuccessToast } from "../Toast/index.js";

const TaskListOptions = ({
  id,
  numberOfOpenTasks,
  numberOfCompletedTasks,
  name,
  batchEdit,
  isCompleted,
  onReopen,
  onMoveToTrash,
  onComplete,
  onEdit,
  projectId,
  className,
  onDoubleClick,
  onVisibilityChange,
  targetSize,
}) => {
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showMoveCopyToProject, setShowMoveCopyToProject] = useState(false);
  const [open, setOpen] = useState(false);
  const renameFlag = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    const nextOpen = true;
    setOpen(nextOpen);
    typeof onVisibilityChange === "function" && onVisibilityChange(nextOpen);
  }, [onVisibilityChange]);
  const handleClose = useCallback(() => {
    const nextOpen = false;
    setOpen(nextOpen);
    typeof onVisibilityChange === "function" && onVisibilityChange(nextOpen);
  }, [onVisibilityChange]);
  const handleMoveToTrash = useCallback(() => {
    onMoveToTrash(id);
  }, [id, onMoveToTrash]);

  const handleComplete = useCallback(() => {
    onComplete(id);
  }, [onComplete, id]);

  const handleReopen = useCallback(() => {
    onReopen(id);
  }, [onReopen, id]);

  const handleShowMoveCopyToProject = useCallback(() => {
    setShowMoveCopyToProject(true);
  }, []);

  const handleCloseMoveCopyToProject = useCallback(() => {
    setShowMoveCopyToProject(false);
  }, []);

  const handleEdit = useCallback(() => {
    renameFlag.current = true;
    handleClose();
  }, [handleClose]);

  const handleShowDuplicateModal = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleCloseDuplicateModal = useCallback(() => {
    setShowDuplicateModal(false);
  }, []);

  const { t } = useTranslation();

  const titleMoveToTrash = useMemo(() => {
    return t("Delete {{name}}?", { name });
  }, [name, t]);

  const descriptionMoveToTrash = useMemo(() => {
    if (!numberOfOpenTasks && !numberOfCompletedTasks)
      return t("This item will remain in Trash for 30 days. To remove it permanently, go to Trash and empty it");

    let message = "";

    if (numberOfOpenTasks && !numberOfCompletedTasks) {
      message = t("This task list has {{numberOfOpenTasks}} open tasks.", {
        numberOfOpenTasks,
      });
    } else if (!numberOfOpenTasks && numberOfCompletedTasks) {
      message = t("This task list has {{numberOfCompletedTasks}} completed tasks.", { numberOfCompletedTasks });
    } else if (numberOfOpenTasks && numberOfCompletedTasks) {
      message = t("This task list has {{numberOfOpenTasks}} open and {{numberOfCompletedTasks}} completed tasks.", {
        numberOfOpenTasks,
        numberOfCompletedTasks,
      });
    }

    return (
      message +
      " " +
      t("Moving this list to Trash will also include all the tasks on the list.") +
      "\n\n" +
      t("The task list and tasks will remain in Trash for 30 days. To remove them permanently, empty the Trash.")
    );
  }, [numberOfOpenTasks, numberOfCompletedTasks, t]);

  const labelButtonMoveToTrash = useMemo(() => {
    const numberOfTasks = numberOfOpenTasks + numberOfCompletedTasks;
    return numberOfTasks ? t("Delete List and {{numberOfTasks}} Tasks", { numberOfTasks }) : t("Delete");
  }, [numberOfOpenTasks, numberOfCompletedTasks, t]);

  const onMoveCopyToProject = useCallback(
    async (attributes) => {
      const { project_id, copy = false, redirect = true } = attributes;
      await dispatch(moveTaskListToProject(projectId, id, { project_id, copy }))
        .then((action) => {
          const { newProjectId = 0, type } = action;

          if (newProjectId > 0) {
            if (redirect && type === MOVE_TASK_LIST_TO_PROJECT_SUCCESS) {
              if (!isCompleted) {
                navigate(`/projects/${newProjectId}`);
              } else {
                navigate(`/projects/${newProjectId}/tasks/archive`);
                handleCloseMoveCopyToProject();
                handleClose();
              }
            } else {
              handleCloseMoveCopyToProject();
              handleClose();
            }
          }
          if (type === MOVE_TASK_LIST_TO_PROJECT_SUCCESS) {
            showSuccessToast(copy ? t("Task list copied to project") : t("Task list moved to project"));
          } else {
            showErrorToast(copy ? t("Failed to copy task list") : t("Failed to move task list"));
          }
        })
        .catch(() => null);
    },
    [dispatch, handleClose, handleCloseMoveCopyToProject, id, navigate, projectId, t, isCompleted]
  );

  const handleTriggerFocus = useCallback(() => {
    if (renameFlag.current && !open) {
      onEdit(id);
      renameFlag.current = false;
    }
  }, [id, onEdit, open]);

  return (
    <div
      onDoubleClick={onDoubleClick}
      className="tasks_options_dropdown"
    >
      {!batchEdit && (
        <Menu
          open={open}
          onClose={handleClose}
          onOpen={handleOpen}
          target={
            <IconButton
              type="button"
              variant="text gray"
              size={targetSize}
              className={classnames(className, { open })}
              active={open}
              onFocus={handleTriggerFocus}
            >
              <TreeDotsIcon />
            </IconButton>
          }
          menuClassName="tw-relative tw-overflow-hidden"
        >
          <List className="tw-py-2">
            <ListItem onClick={handleEdit}>
              <EditIcon />
              {t("Rename")}
            </ListItem>
            {!isCompleted ? (
              <ListItem onClick={handleShowDuplicateModal}>
                <DuplicateIcon />
                {t("Duplicate")}
              </ListItem>
            ) : null}
            <ListItem onClick={handleShowMoveCopyToProject}>
              <CopyIcon />
              {t("Move/Copy to Project")}
            </ListItem>
            {isCompleted ? (
              <ConfirmLink
                className="tw-no-underline"
                data-qa-id="reopen-task-list"
                title={`${t("Reopen")} ${name}?`}
                description={t(
                  "This will restore the list and all the tasks that were open before the list was completed."
                )}
                confirmLabel={t("Reopen")}
                onClick={handleReopen}
              >
                <ListItem>
                  <CheckboxBlankTogglerIcon />
                  {t("Reopen")}
                </ListItem>
              </ConfirmLink>
            ) : (
              <ConfirmLink
                className="tw-no-underline"
                data-qa-id="complete-task-list"
                title={`${t("Complete")} ${name}?`}
                description={t("This will move the list and its tasks to Completed tasks.")}
                confirmLabel={t("Complete")}
                onClick={handleComplete}
              >
                <ListItem>
                  <TaskListCompleteIcon />
                  {t("Complete")}
                </ListItem>
              </ConfirmLink>
            )}
            <ListSeparator className="tw-my-2.5 tw-mx-4" />
            <ConfirmLink
              className="tw-no-underline"
              data-qa-id="trash-task-list"
              title={titleMoveToTrash}
              description={descriptionMoveToTrash}
              confirmLabel={labelButtonMoveToTrash}
              onClick={handleMoveToTrash}
            >
              <ListItem className="tw-no-underline">
                <TrashIcon />
                {t("Delete")}
              </ListItem>
            </ConfirmLink>
          </List>
        </Menu>
      )}
      <Sheet
        open={showMoveCopyToProject}
        onClose={handleCloseMoveCopyToProject}
      >
        <PrefetchAllProjectsContainer
          onCancel={handleCloseMoveCopyToProject}
          open={showMoveCopyToProject}
          projectId={projectId}
          onMoveCopyToProject={onMoveCopyToProject}
        />
      </Sheet>
      <TaskListDuplicateDialog
        projectId={projectId}
        id={id}
        open={showDuplicateModal}
        name={name}
        onCancel={handleCloseDuplicateModal}
        onDuplicateSuccess={handleCloseDuplicateModal}
      />
    </div>
  );
};

TaskListOptions.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  numberOfOpenTasks: PropTypes.number,
  numberOfCompletedTasks: PropTypes.number,
  onMoveToTrash: PropTypes.func,
  onComplete: PropTypes.func,
  onEdit: PropTypes.func,
  onReopen: PropTypes.func,
  isCompleted: PropTypes.bool,
  batchEdit: PropTypes.bool,
  className: PropTypes.string,
  onDoubleClick: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  targetSize: PropTypes.string,
};

TaskListOptions.defaultProps = {
  numberOfOpenTasks: 0,
  onMoveToTrash: () => null,
  onVisibilityChange: () => null,
  onReopen: () => null,
  onEdit: () => null,
  onComplete: () => null,
  isCompleted: false,
  batchEdit: false,
  className: "",
  targetSize: "small",
};

TaskListOptions.defaultName = "TaskListOptions";

export default TaskListOptions;
